import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
  useTheme,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { IUser } from '../../types/damTypes';
import { hasPermission } from '../../utils/permissionUtils';
import ActivityDetailDrawer from './ActivityDetailDrawer';
import { apiErrorMessage, launchApi } from './api';
import { departmentKey } from './departments';
import { compareActivities, displayText, launchStatusText, makeId, MyWorkGroup, myWorkGroup, myWorkGroupOrder, today } from './helpers';
import LaunchCard from './LaunchCard';
import { help } from './helpTexts';
import LaunchWizard from './LaunchWizard';
import LaunchSettingsView from './settings/LaunchSettingsView';
import { cardSx, launchColors, pageTitleSx } from './launchTheme';
import { ActivityProjection, Assignee, InternalNotification, LaunchSummary, MyWork } from './types';
import ActivityRow from './ui/ActivityRow';
import DepartmentBadge from './ui/DepartmentBadge';
import InfoTip from './ui/InfoTip';
import WorkspaceView from './WorkspaceView';
import YearbookView from './yearbook/YearbookView';
import ProductInfoView from './productInfo/ProductInfoView';
import { onOpenProductInfo } from './productInfo/productInfoLink';
import AlcoholInfoView from './alcoholInfo/AlcoholInfoView';
import { onOpenAlcoholInfo } from './alcoholInfo/alcoholInfoLink';

interface NyhetslanseringarModuleProps {
  onClose: () => void;
  currentUser: IUser | null;
}

type ModuleTab = 'launches' | 'mywork' | 'yearbook' | 'productinfo' | 'alcoholinfo';
const selectedLaunchKey = 'nyhetslanseringar.selectedLaunchId.v1';

const NyhetslanseringarModule: React.FC<NyhetslanseringarModuleProps> = ({ onClose, currentUser }) => {
  const outerTheme = useTheme();
  const moduleTheme = useMemo(() => createTheme(outerTheme, {
    components: {
      MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, fontSize: 14, minHeight: 44 } } },
      MuiToggleButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
    },
  }), [outerTheme]);
  const canRead = hasPermission(currentUser, 'nyhetslanseringar:read');
  const canWrite = hasPermission(currentUser, 'nyhetslanseringar:write');
  const canApprove = hasPermission(currentUser, 'nyhetslanseringar:approve');
  const canAdminAll = hasPermission(currentUser, 'admin:all');
  const canAdminSettings = canAdminAll || hasPermission(currentUser, 'nyhetslanseringar:admin');
  const canEditYearbook = canAdminAll || hasPermission(currentUser, 'nyhetslanseringar:calendar');
  const canSeeProductInfo = hasPermission(currentUser, 'nyhetslanseringar:productInfo');
  const canSeeAlcoholInfo = hasPermission(currentUser, 'nyhetslanseringar:alcoholInfo');
  const [tab, setTab] = useState<ModuleTab>('launches');
  const [yearbookOpened, setYearbookOpened] = useState(false);
  const [productInfoOpened, setProductInfoOpened] = useState(false);
  const [productInfoTarget, setProductInfoTarget] = useState<string | null>(null);
  const [alcoholInfoOpened, setAlcoholInfoOpened] = useState(false);
  const [alcoholInfoTarget, setAlcoholInfoTarget] = useState<string | null>(null);
  const [selectedLaunchId, setSelectedLaunchId] = useState<string | null>(() => {
    try { return localStorage.getItem(selectedLaunchKey); } catch { return null; }
  });
  const [launches, setLaunches] = useState<LaunchSummary[]>([]);
  const [myWork, setMyWork] = useState<MyWork | null>(null);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [notifications, setNotifications] = useState<InternalNotification[]>([]);
  const [dueReminders, setDueReminders] = useState(0);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [drawer, setDrawer] = useState<{ launchId: string; activityId: string } | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [activationSkippedId, setActivationSkippedId] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [membership, setMembership] = useState<{ departments: string[]; choices: string[] }>({ departments: [], choices: [] });
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [savingDepartments, setSavingDepartments] = useState(false);

  const loadSupportingData = useCallback(async () => {
    if (!canRead) return;
    const [peopleResult, notificationResult, departmentResult, reminderResult] = await Promise.allSettled([
      launchApi.assignees(),
      launchApi.notifications(),
      launchApi.myDepartments(),
      launchApi.yearbookReminders(),
    ]);
    if (peopleResult.status === 'fulfilled') setAssignees(peopleResult.value);
    if (reminderResult.status === 'fulfilled') {
      const asOf = today();
      setDueReminders(reminderResult.value.filter((item) => item.date <= asOf).length);
    }
    if (notificationResult.status === 'fulfilled') {
      setNotifications(Array.from(new Map(notificationResult.value.map((item) => [item.dedupeKey, item])).values()));
    }
    if (departmentResult.status === 'fulfilled') {
      setMembership(departmentResult.value);
      setMembershipError(null);
    } else {
      setMembershipError(apiErrorMessage(departmentResult.reason, 'Avdelningarna kunde inte hämtas.'));
    }
  }, [canRead]);

  const loadLaunches = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      const response = await launchApi.list(q, status);
      setLaunches(response.launches);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Lanseringarna kunde inte hämtas.'));
    } finally {
      setLoading(false);
    }
  }, [canRead, q, status]);

  const loadMyWork = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      setMyWork(await launchApi.myWork());
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Ditt arbete kunde inte hämtas.'));
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  useEffect(() => { void loadSupportingData(); }, [loadSupportingData]);
  useEffect(() => {
    const timer = window.setTimeout(() => { void loadLaunches(); }, 250);
    return () => window.clearTimeout(timer);
  }, [loadLaunches, refreshToken]);
  useEffect(() => { if (tab === 'mywork') void loadMyWork(); }, [loadMyWork, refreshToken, tab]);
  useEffect(() => { if (tab === 'yearbook') setYearbookOpened(true); }, [tab]);
  useEffect(() => { if (tab === 'productinfo') setProductInfoOpened(true); }, [tab]);
  useEffect(() => onOpenProductInfo((productId) => {
    if (!canSeeProductInfo) return;
    setProductInfoTarget(productId);
    setTab('productinfo');
  }), [canSeeProductInfo]);
  const clearProductInfoTarget = useCallback(() => setProductInfoTarget(null), []);
  useEffect(() => { if (tab === 'alcoholinfo') setAlcoholInfoOpened(true); }, [tab]);
  useEffect(() => onOpenAlcoholInfo((productId) => {
    if (!canSeeAlcoholInfo) return;
    setAlcoholInfoTarget(productId);
    setTab('alcoholinfo');
  }), [canSeeAlcoholInfo]);
  const clearAlcoholInfoTarget = useCallback(() => setAlcoholInfoTarget(null), []);
  useEffect(() => {
    try {
      if (selectedLaunchId) localStorage.setItem(selectedLaunchKey, selectedLaunchId);
      else localStorage.removeItem(selectedLaunchKey);
    } catch { /* val av arbetsyta fungerar ändå under sessionen */ }
  }, [selectedLaunchId]);

  const attentionCount = useMemo(
    () => new Set(notifications.map((item) => `${item.launchId}:${item.activityId}`)).size,
    [notifications],
  );

  const changed = () => {
    setRefreshToken((value) => value + 1);
    void loadSupportingData();
  };

  const toggleDepartment = async (name: string, selected: boolean) => {
    const next = selected
      ? [...membership.departments.filter((item) => departmentKey(item) !== departmentKey(name)), name]
      : membership.departments.filter((item) => departmentKey(item) !== departmentKey(name));
    const previous = membership;
    setMembership({ ...membership, departments: next });
    setSavingDepartments(true);
    setMembershipError(null);
    try {
      setMembership(await launchApi.saveDepartments(next));
      changed();
    } catch (requestError) {
      setMembership(previous);
      setMembershipError(apiErrorMessage(requestError, 'Avdelningarna kunde inte sparas.'));
    } finally {
      setSavingDepartments(false);
    }
  };

  const quickComplete = async (launchId: string, activityId: string) => {
    setCompletingId(`${launchId}:${activityId}`);
    setError(null);
    try {
      const workspace = await launchApi.workspace(launchId);
      await launchApi.activityCommand(launchId, activityId, {
        commandId: makeId('complete'),
        type: 'complete',
        expectedVersion: workspace.expectedVersion,
        actuallyOccurredAt: today(),
      });
      setNotice('Aktiviteten har markerats klar.');
      changed();
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) {
        setNotice('Någon annan hann ändra lanseringen. Listan har laddats om.');
        changed();
      } else {
        setError(apiErrorMessage(requestError, 'Aktiviteten kunde inte markeras klar.'));
      }
    } finally {
      setCompletingId(null);
    }
  };

  if (!canRead) {
    return <Box sx={{ p: 2 }}><Alert severity="warning" action={<Button onClick={onClose}>Stäng</Button>}>Du saknar läsbehörighet för nyhetslanseringar.</Alert></Box>;
  }

  return (
    <ThemeProvider theme={moduleTheme}>
      <Box sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: launchColors.page,
        color: launchColors.text,
        pt: { xs: 1.5, sm: 2, lg: 3 },
        pl: { xs: 1.5, sm: 2, lg: 3 },
        pr: { xs: 1.5, sm: 8 },
        pb: 10,
      }}>
        {!selectedLaunchId && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <RocketLaunchIcon sx={{ color: launchColors.navy }} />
            <Typography component="h1" sx={[pageTitleSx, { flex: 1 }]}>Nyhetslanseringar</Typography>
            <Button onClick={onClose}>Stäng</Button>
          </Stack>
        )}

        {selectedLaunchId ? (
          <WorkspaceView
            launchId={selectedLaunchId}
            currentUserId={currentUser?._id || ''}
            canWrite={canWrite}
            canAdminAll={canAdminAll}
            assignees={assignees}
            notifications={notifications}
            myDepartments={membership.departments}
            refreshToken={refreshToken}
            activationSkipped={activationSkippedId === selectedLaunchId}
            onBack={() => {
              setSelectedLaunchId(null);
              setActivationSkippedId(null);
            }}
            onOpenActivity={(activityId) => setDrawer({ launchId: selectedLaunchId, activityId })}
            onChanged={changed}
          />
        ) : settingsOpen ? (
          <LaunchSettingsView assignees={assignees} onClose={() => setSettingsOpen(false)} />
        ) : (
          <Stack spacing={2}>
            <Box sx={{ borderBottom: `1px solid ${launchColors.divider}` }}>
              <Tabs value={tab} onChange={(_event, value: ModuleTab) => setTab(value)} aria-label="Nyhetslanseringar">
                <Tab value="launches" label="Lanseringar" />
                <Tab
                  value="mywork"
                  label={<TabLabel text="Mitt arbete" count={attentionCount} title="behöver åtgärdas" />}
                  aria-label={attentionCount ? `Mitt arbete, ${attentionCount} behöver åtgärdas` : 'Mitt arbete'}
                />
                <Tab
                  value="yearbook"
                  label={<TabLabel text="Årskalender" count={dueReminders} title={dueReminders === 1 ? 'påminnelse om varuprov' : 'påminnelser om varuprover'} />}
                  aria-label={dueReminders ? `Årskalender, ${dueReminders} ${dueReminders === 1 ? 'påminnelse om varuprov' : 'påminnelser om varuprover'}` : 'Årskalender'}
                />
                {canSeeProductInfo && <Tab value="productinfo" label="Produktinformation Dabas" />}
                {canSeeAlcoholInfo && <Tab value="alcoholinfo" label="Produktinfo Alkohol" />}
              </Tabs>
            </Box>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {tab === 'launches' && (
              <LaunchList
                launches={launches}
                assignees={assignees}
                q={q}
                status={status}
                loading={loading}
                canCreate={canWrite}
                canAdmin={canAdminSettings}
                onQ={setQ}
                onStatus={setStatus}
                onCreate={() => setWizardOpen(true)}
                onSettings={() => setSettingsOpen(true)}
                onSelect={setSelectedLaunchId}
              />
            )}
            {(tab === 'yearbook' || yearbookOpened) && (
              <Box sx={{ display: tab === 'yearbook' ? 'block' : 'none' }}>
                <YearbookView
                  canEdit={canEditYearbook}
                  canImport={canAdminSettings}
                  canOpenProductInfo={canSeeProductInfo}
                  canOpenAlcoholInfo={canSeeAlcoholInfo}
                  currentUserId={currentUser?._id || ''}
                  onDueReminders={setDueReminders}
                  active={tab === 'yearbook'}
                  people={assignees}
                />
              </Box>
            )}
            {canSeeProductInfo && (tab === 'productinfo' || productInfoOpened) && (
              <Box sx={{ display: tab === 'productinfo' ? 'block' : 'none' }}>
                <ProductInfoView
                  canEdit={canEditYearbook}
                  canImport={canAdminSettings}
                  people={assignees}
                  openProductId={productInfoTarget}
                  onOpened={clearProductInfoTarget}
                />
              </Box>
            )}
            {canSeeAlcoholInfo && (tab === 'alcoholinfo' || alcoholInfoOpened) && (
              <Box sx={{ display: tab === 'alcoholinfo' ? 'block' : 'none' }}>
                <AlcoholInfoView
                  canEdit={canSeeAlcoholInfo}
                  canAdmin={canAdminSettings}
                  people={assignees}
                  openProductId={alcoholInfoTarget}
                  onOpened={clearAlcoholInfoTarget}
                />
              </Box>
            )}
            {tab === 'mywork' && (
              <MyWorkView
                data={myWork}
                loading={loading}
                notifications={notifications}
                membership={membership}
                membershipError={membershipError}
                savingDepartments={savingDepartments}
                canWrite={canWrite}
                completingId={completingId}
                onOpen={(launchId, activityId) => setDrawer({ launchId, activityId })}
                onComplete={quickComplete}
                onToggleDepartment={toggleDepartment}
              />
            )}
          </Stack>
        )}

        <LaunchWizard
          open={wizardOpen}
          currentUser={currentUser}
          assignees={assignees}
          onClose={() => setWizardOpen(false)}
          onCreated={(launchId, activationSkipped, activationError) => {
            setWizardOpen(false);
            setActivationSkippedId(activationSkipped ? launchId : null);
            if (activationError) setNotice(activationError);
            setSelectedLaunchId(launchId);
            changed();
          }}
        />
        <ActivityDetailDrawer
          launchId={drawer?.launchId || null}
          activityId={drawer?.activityId || null}
          currentUserId={currentUser?._id || ''}
          assignees={assignees}
          canApprove={canApprove}
          canAdminAll={canAdminAll}
          onClose={() => setDrawer(null)}
          onChanged={changed}
        />
        <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} message={notice || ''} />
      </Box>
    </ThemeProvider>
  );
};

const LaunchList = ({
  launches,
  assignees,
  q,
  status,
  loading,
  canCreate,
  canAdmin,
  onQ,
  onStatus,
  onCreate,
  onSettings,
  onSelect,
}: {
  launches: LaunchSummary[];
  assignees: Assignee[];
  q: string;
  status: string;
  loading: boolean;
  canCreate: boolean;
  canAdmin: boolean;
  onQ: (value: string) => void;
  onStatus: (value: string) => void;
  onCreate: () => void;
  onSettings: () => void;
  onSelect: (id: string) => void;
}) => {
  const asOf = today();
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <TextField size="small" label="Sök namn, produkt eller artikelnummer" value={q} onChange={(event) => onQ(event.target.value)} sx={{ flex: '1 1 260px', minWidth: 0, bgcolor: launchColors.card }} />
        <TextField select size="small" label="Status" value={status} onChange={(event) => onStatus(event.target.value)} sx={{ flex: '0 0 180px', bgcolor: launchColors.card }}>
          <MenuItem value="">Alla statusar</MenuItem>
          {Object.entries(launchStatusText).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 0 auto', ml: 'auto' }}>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreate}
              sx={{ flex: '0 0 auto', minWidth: 'max-content', height: 40, px: 2.25, whiteSpace: 'nowrap', overflow: 'visible' }}
            >
              Skapa lansering
            </Button>
          )}
          {canAdmin && <IconButton aria-label="Inställningar" onClick={onSettings}><SettingsOutlinedIcon /></IconButton>}
          <InfoTip text={help.listCards} />
        </Stack>
      </Stack>
      {loading && <LinearProgress />}
      {!loading && launches.length === 0 && (
        <Box sx={[cardSx, { p: 3, textAlign: 'center' }]}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: launchColors.navy }}>Inga lanseringar hittades</Typography>
          <Typography sx={{ color: launchColors.textSecondary }}>Justera filtren eller skapa en ny lansering.</Typography>
          {canCreate && <Button sx={{ mt: 2, minWidth: 'max-content', whiteSpace: 'nowrap' }} variant="contained" startIcon={<AddIcon />} onClick={onCreate}>Skapa lansering</Button>}
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
        {launches.map((launch) => (
          <LaunchCard
            key={launch.id}
            launch={launch}
            asOf={asOf}
            ownerName={assignees.find((person) => person.id === launch.ownerUserId)?.name || (launch.ownerUserId ? 'Okänd person' : 'Ingen ägare')}
            onSelect={() => onSelect(launch.id)}
          />
        ))}
      </Box>
    </Stack>
  );
};

const TabLabel: React.FC<{ text: string; count: number; title: string }> = ({ text, count, title }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <span>{text}</span>
    {count > 0 && (
      <Box
        component="span"
        aria-hidden
        title={`${count} ${title}`}
        sx={{ minWidth: 20, height: 20, px: 0.5, borderRadius: '10px', bgcolor: launchColors.dangerText, color: '#FFFFFF', fontSize: 12, fontWeight: 700, lineHeight: '20px', textAlign: 'center' }}
      >
        {count > 99 ? '99+' : count}
      </Box>
    )}
  </Stack>
);

const myWorkGroupHelp: Record<MyWorkGroup, typeof help.myWorkLate> = {
  Försenat: help.myWorkLate,
  'Behöver åtgärd': help.myWorkAction,
  Idag: help.myWorkToday,
  Kommande: help.myWorkUpcoming,
  'Utan datum': help.myWorkUndated,
  Blockerat: help.myWorkBlocked,
  Väntar: help.myWorkWaiting,
};

const myWorkGroupColor: Record<MyWorkGroup, string> = {
  Försenat: launchColors.dangerText,
  'Behöver åtgärd': launchColors.plumDark,
  Idag: launchColors.warningText,
  Kommande: launchColors.navy,
  'Utan datum': launchColors.textSecondary,
  Blockerat: launchColors.dangerText,
  Väntar: launchColors.sandDark,
};

const notificationTypeText: Record<InternalNotification['type'], string> = {
  active_blocker: 'Blockerad',
  overdue: 'Försenad',
  due_soon: 'Snart',
  approval_required: 'Godkännande',
};

const notificationTypeColor: Record<InternalNotification['type'], string> = {
  active_blocker: launchColors.dangerText,
  overdue: launchColors.dangerText,
  due_soon: launchColors.warningText,
  approval_required: launchColors.sandDark,
};

const MyWorkView = ({
  data,
  loading,
  notifications,
  membership,
  membershipError,
  savingDepartments,
  canWrite,
  completingId,
  onOpen,
  onComplete,
  onToggleDepartment,
}: {
  data: MyWork | null;
  loading: boolean;
  notifications: InternalNotification[];
  membership: { departments: string[]; choices: string[] };
  membershipError: string | null;
  savingDepartments: boolean;
  canWrite: boolean;
  completingId: string | null;
  onOpen: (launchId: string, activityId: string) => void;
  onComplete: (launchId: string, activityId: string) => void;
  onToggleDepartment: (name: string, selected: boolean) => void;
}) => {
  const asOf = data?.asOf || today();
  const rows = useMemo<ActivityProjection[]>(() => data?.groups.flatMap((group) => group.activities.map((activity) => ({
    ...activity,
    launchId: group.launchId,
    launchName: group.launchName,
  }))) || [], [data]);
  const grouped = myWorkGroupOrder.map((name) => ({
    name,
    rows: rows.filter((activity) => myWorkGroup(activity, asOf) === name).sort(compareActivities),
  }));
  return (
    <Stack spacing={2}>
      <Box component="section" aria-label="Mina avdelningar" sx={[cardSx, { p: 1.75 }]}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700, color: launchColors.navy }}>Mina avdelningar</Typography>
          <InfoTip text={help.myDepartments} />
        </Stack>
        {membershipError && <Alert severity="error" sx={{ mb: 1 }}>{membershipError}</Alert>}
        {membership.choices.length === 0 && !membershipError && (
          <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga avdelningar finns i mallen.</Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
          {membership.choices.map((name) => {
            const checked = membership.departments.some((item) => departmentKey(item) === departmentKey(name));
            return (
              <FormControlLabel
                key={departmentKey(name)}
                control={(
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={savingDepartments}
                    onChange={() => onToggleDepartment(name, !checked)}
                    inputProps={{ 'aria-label': displayText(name) }}
                  />
                )}
                label={<DepartmentBadge name={name} size={22} />}
              />
            );
          })}
        </Box>
      </Box>
      {loading && <LinearProgress />}
      {notifications.length > 0 && (
        <Box component="section" aria-label="Uppmärksamhet" sx={[cardSx, { p: 1.75 }]}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700, color: launchColors.navy }}>Uppmärksamhet</Typography>
            <InfoTip text={help.myWorkAttention} />
          </Stack>
          <Stack spacing={0.5}>
            {notifications.slice(0, 6).map((item) => (
              <ButtonBase
                key={item.dedupeKey}
                onClick={() => onOpen(item.launchId, item.activityId)}
                sx={{ justifyContent: 'flex-start', gap: 1, textAlign: 'left', borderRadius: '6px', px: 1, py: 0.75, '&:hover': { bgcolor: launchColors.section } }}
              >
                <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: notificationTypeColor[item.type], minWidth: 92 }}>{notificationTypeText[item.type]}</Typography>
                <Typography component="span" sx={{ fontSize: 14, color: launchColors.text }}>{displayText(item.title)}</Typography>
              </ButtonBase>
            ))}
          </Stack>
        </Box>
      )}
      <Stack direction="row" spacing={0.5} alignItems="center">
        <InfoTip text={help.myWorkIntro} />
        <InfoTip text={help.myWorkComplete} />
        {data && rows.length > 0 && (
          <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>
            {data.totalCount} aktiviteter för dig · {data.actionRequiredCount} behöver åtgärd
          </Typography>
        )}
      </Stack>
      {!loading && rows.length === 0 && <Alert severity="info">Inget arbete för dina avdelningar i aktiva lanseringar. Välj avdelning ovan om listan ska fyllas.</Alert>}
      {grouped.map((group) => group.rows.length > 0 && (
        <Box key={group.name} component="section" aria-label={`${group.name}, ${group.rows.length}`} sx={[cardSx, { overflow: 'hidden' }]}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.75, py: 1.1, bgcolor: launchColors.section, borderBottom: `1px solid ${launchColors.divider}` }}>
            <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700, color: myWorkGroupColor[group.name] }}>{group.name}</Typography>
            <InfoTip text={myWorkGroupHelp[group.name]} />
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary, bgcolor: launchColors.card, border: `1px solid ${launchColors.divider}`, borderRadius: '10px', px: 0.9, lineHeight: '18px' }}>{group.rows.length}</Typography>
          </Stack>
          <Box role="list" aria-label={group.name}>
            {group.rows.map((activity) => {
              const key = `${activity.launchId}:${activity.id}`;
              const canComplete = canWrite && activity.allowedActions.includes('complete');
              return (
                <ActivityRow
                  key={key}
                  activity={activity}
                  asOf={asOf}
                  launchName={activity.launchName}
                  onOpen={() => onOpen(activity.launchId, activity.id)}
                  action={canComplete ? (
                    <ButtonBase
                      onClick={() => onComplete(activity.launchId, activity.id)}
                      disabled={completingId === key}
                      aria-label={`Markera ${activity.title} klar`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, height: 30, borderRadius: '8px', fontSize: 13, fontWeight: 600, color: launchColors.mossDark, border: `1px solid ${launchColors.moss}`, bgcolor: launchColors.card, whiteSpace: 'nowrap', '&:hover': { bgcolor: launchColors.mossLight }, '&.Mui-disabled': { opacity: 0.5 } }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                      {completingId === key ? 'Sparar…' : 'Markera klar'}
                    </ButtonBase>
                  ) : undefined}
                />
              );
            })}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

export default NyhetslanseringarModule;
